import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useMemoizedFn } from 'ahooks';
import { Table, TableProps, TableAddon, TableColumn } from 'tea-component';
import AddableTable from '@gp/components/AddableTable';
import ReportOperationCard, { IReportOperationCardProps } from '../ReportOperationCard';

import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';

interface IReportTableProps extends IBaseProps, TableProps, Omit<IReportOperationCardProps, 'onDelete'> {
  // 是否表格内部loading
  isInnerLoading?: boolean;
  actions?: React.ReactElement | null;
  disabledAddBtn?: boolean;
  addBtnTips?: string | React.ReactElement | null;
  addons?: TableAddon[];
  columns: TableColumn[];
  records: IRecord[];
  onAdd?: () => void;
  onDelete?: (selection: string[]) => void;
}

const { selectable } = Table.addons;

const ReportTable: React.FC<IReportTableProps> = (props) => {
  const {
    style,
    className,
    // 表格模块title
    title,
    isSupportRenameTitle,
    onTitleChange,
    // 是否loading
    isLoading,
    // 是否表格内部loading
    isInnerLoading,
    // 操作工具栏
    actions,
    // 表格record主键
    recordKey,
    // 是否禁用Add按钮
    disabledAddBtn,
    // Add按钮的hover提示
    addBtnTips,
    // 是否展示Refresh Data按钮
    showRefreshData,
    // 表格addons
    addons: addonsProps,
    // 表格columns
    columns,
    // 表格records
    records,
    // 点击Add的回调
    onAdd,
    // 点击Delete按钮，并且二次确认时点击Confirm的回调
    onDelete,
    // 点击Refresh Data按钮的回调
    onRefreshData,
  } = props;

  const [selection, setSelection] = useState<string[]>([]);

  const addons = useMemo<TableAddon[]>(() => {
    const addonsPropsOthers =
      addonsProps?.filter((item) => {
        const addonInfo = (item as any)?.getInfo();
        return addonInfo?.name !== 'selectable';
      }) || [];
    return [
      ...addonsPropsOthers,
      selectable({
        all: Array.isArray(records) && records.length > 0,
        value: selection,
        onChange: setSelection,
      }),
    ];
  }, [addonsProps, selection, records]);

  const handleDelete = useMemoizedFn(() => {
    if (!Array.isArray(selection)) return;
    setSelection([]);
    onDelete?.([...selection]);
  });

  return (
    <ReportOperationCard
      style={style}
      className={classnames('dbg-dps-report__report-table-card', className)}
      title={title}
      isSupportRenameTitle={isSupportRenameTitle}
      onTitleChange={onTitleChange}
      isLoading={isLoading}
      actions={actions}
      showDelete={!!records?.length}
      showRefreshData={showRefreshData}
      disabledDelete={!selection?.length}
      disabledRefreshData={isInnerLoading}
      onDelete={handleDelete}
      onRefreshData={onRefreshData}
    >
      <AddableTable
        tableClassName="dbg-selectable-table"
        isInnerLoading={isInnerLoading}
        disabledAddBtn={disabledAddBtn}
        addBtnTips={addBtnTips}
        addons={addons}
        columns={columns}
        records={records}
        recordKey={recordKey}
        onAdd={onAdd}
      />
    </ReportOperationCard>
  );
};

export default ReportTable;
